<script setup lang="ts">
import { computed, ref } from 'vue';

import { AppStandardModal } from '@libs/ui';
import type { AutoCompleteSuggestion } from '@libs/trip-planner-interfaces';

import { AutoCompleteType } from '../autoComplete';
import type { DestinationAutocompleteType } from '../autoComplete';

import DestinationSelector from './DestinationSelector.vue';

interface DestinationSelectorModalProps {
  searchText: string;
  autocompleteType?: DestinationAutocompleteType;
}

const props = withDefaults(defineProps<DestinationSelectorModalProps>(), {
  autocompleteType: AutoCompleteType.Destination,
});
interface DestinationSelectorModalEmits {
  (
    eventName: 'change-destination',
    newDestination: AutoCompleteSuggestion | null | undefined
  ): void;
  (
    eventName: 'select-my-position',
    autocompleteType: DestinationAutocompleteType
  ): void;
  (eventName: 'close-modal'): void;
}

const emit = defineEmits<DestinationSelectorModalEmits>();

const newDestination = ref<AutoCompleteSuggestion | null | undefined>(
  undefined
);

const isButtonDisabled = computed<boolean>(
  () =>
    newDestination.value === null ||
    (props.searchText === '' && newDestination.value === undefined)
);

function changeDestination(): void {
  emit('change-destination', newDestination.value);
}

function selectMyPosition(autocompleteType: DestinationAutocompleteType): void {
  emit('select-my-position', autocompleteType);
}

function saveDestination(destination: AutoCompleteSuggestion): void {
  newDestination.value = destination;
}

function clearSavedDestination(): void {
  newDestination.value = null;
}
</script>

<template>
  <AppStandardModal
    :is-button-disabled="isButtonDisabled"
    @close="emit('close-modal')"
    @confirm="changeDestination"
  >
    <template #title>
      {{ $t('planner.where_to_travel') }}
    </template>

    <template #body>
      <DestinationSelector
        :search-text="props.searchText"
        :autocomplete-type="props.autocompleteType"
        @select-destination="saveDestination"
        @select-my-position="selectMyPosition"
        @clear-selection="clearSavedDestination"
      />
    </template>
    <template #button>
      {{ $t('planner.done') }}
    </template>
  </AppStandardModal>
</template>
